// Copyright 2020 The Nakama Authors
//
// Licensed under the Apache License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License.
// You may obtain a copy of the License at
//
// http://www.apache.org/licenses/LICENSE-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
// See the License for the specific language governing permissions and
// limitations under the License.

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { ChatMessage, ChatMessageCode, UpdateChatMessageRequest } from '../../types/chatMessages';
import { UserRole } from '../../types/auth';
import { consoleService } from '../../services/consoleService';
import { authService } from '../../services/authService';
import { useConfirmDialog } from '../../hooks/useConfirmDialog';
import ConfirmDialog from '../ConfirmDialog';
import './ChatMessageDetail.scss';

// SVG Icons
const RedTriangleIcon = () => (
  <img src="/static/svg/red-triangle.svg" alt="" width="16" height="16" className="mr-2" />
);

const LoadingSpinner = () => (
  <div className="spinner-border spinner-border-sm" role="status">
    <span className="visually-hidden">Loading...</span>
  </div>
);

const ChatMessageDetail: React.FC = () => {
  const { messageId } = useParams<{ messageId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { dialogState, openConfirmDialog, closeConfirmDialog, handleConfirm } = useConfirmDialog();

  const [message, setMessage] = useState<ChatMessage | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);

  const sessionRole = authService.getSessionRole();
  const channelId = searchParams.get('channel_id') || '';

  useEffect(() => {
    if (messageId && channelId) {
      loadMessage();
    }
  }, [messageId, channelId]);

  const loadMessage = useCallback(async () => {
    if (!messageId || !channelId) return;

    setLoading(true);
    setError('');

    try {
      const result = await consoleService.getChatMessage('', messageId, channelId);
      setMessage(result);
      setEditContent(result.content || '');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load message');
    } finally {
      setLoading(false);
    }
  }, [messageId, channelId]);

  const handleBackToMessages = () => {
    navigate('/chat-messages');
  };

  const handleEditMessage = () => {
    setEditing(true);
    setEditContent(message?.content || '');
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setEditContent(message?.content || '');
  };

  const handleSaveEdit = async () => {
    if (!message || !editContent.trim()) return;

    setSaving(true);
    setError('');

    try {
      const request: UpdateChatMessageRequest = {
        message_id: message.message_id,
        channel_id: message.channel_id,
        content: editContent.trim()
      };

      const updatedMessage = await consoleService.updateChatMessage('', request);
      setMessage(prev => prev ? { ...prev, content: editContent.trim(), update_time: new Date().toISOString() } : null);
      setEditing(false);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to update message');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteMessage = () => {
    if (!message) return;

    openConfirmDialog(
      'Delete Message',
      `Are you sure you want to delete this message? This action cannot be undone.`,
      async () => {
        try {
          await consoleService.deleteChatMessage('', {
            message_id: message.message_id,
            channel_id: message.channel_id
          });
          navigate('/chat-messages');
        } catch (err: any) {
          setError(err.response?.data?.message || err.message || 'Failed to delete message');
        }
      }
    );
  };

  const editAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const deleteAllowed = () => {
    return sessionRole === UserRole.USER_ROLE_ADMIN;
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleString();
  };

  const getMessageTypeText = (code: number) => {
    switch (code) {
      case ChatMessageCode.CHANNEL_JOIN: return 'Join';
      case ChatMessageCode.CHANNEL_LEAVE: return 'Leave';
      case ChatMessageCode.CHANNEL_MESSAGE: return 'Message';
      case ChatMessageCode.CHANNEL_UPDATE: return 'Update';
      case ChatMessageCode.CHANNEL_REMOVE: return 'Remove';
      default: return 'Unknown';
    }
  };

  const getMessageTypeBadgeClass = (code: number) => {
    switch (code) {
      case ChatMessageCode.CHANNEL_JOIN: return 'join';
      case ChatMessageCode.CHANNEL_LEAVE: return 'leave';
      case ChatMessageCode.CHANNEL_MESSAGE: return 'message';
      case ChatMessageCode.CHANNEL_UPDATE: return 'update';
      case ChatMessageCode.CHANNEL_REMOVE: return 'remove';
      default: return 'unknown';
    }
  };

  const getChannelType = (message: ChatMessage) => {
    if (message.room_name) return 'Room';
    if (message.group_id) return 'Group';
    if (message.user_id_one && message.user_id_two) return 'Direct';
    return 'Unknown';
  };

  const getChannelInfo = (message: ChatMessage) => {
    if (message.room_name) return message.room_name;
    if (message.group_id) return message.group_id;
    if (message.user_id_one && message.user_id_two) return `${message.user_id_one} ↔ ${message.user_id_two}`;
    return message.channel_id;
  };

  if (loading) {
    return (
      <div className="chat-message-detail-container">
        <div className="alert alert-info">
          <LoadingSpinner />
          <strong className="ms-2">Loading message...</strong>
        </div>
      </div>
    );
  }

  if (!message) {
    return (
      <div className="chat-message-detail-container">
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Message not found</strong>
        </div>
      </div>
    );
  }

  return (
    <div className="chat-message-detail-container">
      <div className="message-detail-header">
        <div className="header-actions">
          <button
            type="button"
            className="btn btn-outline-secondary back-btn"
            onClick={handleBackToMessages}
          >
            ← Back to Messages
          </button>
        </div>
        <h2 className="message-title">Chat Message Details</h2>
        <h6 className="message-subtitle">Message ID: {message.message_id}</h6>
      </div>

      <div className="message-info-section">
        <div className="info-grid">
          <div className="info-item">
            <label>Message Type:</label>
            <span className={`type-badge ${getMessageTypeBadgeClass(message.code)}`}>
              {getMessageTypeText(message.code)}
            </span>
          </div>
          <div className="info-item">
            <label>Channel Type:</label>
            <span className="channel-type">{getChannelType(message)}</span>
          </div>
          <div className="info-item">
            <label>Channel Info:</label>
            <code className="channel-info">{getChannelInfo(message)}</code>
          </div>
          <div className="info-item">
            <label>Persistent:</label>
            <span className={`persistent-badge ${message.persistent ? 'persistent' : 'temporary'}`}>
              {message.persistent ? 'Yes' : 'No'}
            </span>
          </div>
          <div className="info-item">
            <label>Sender:</label>
            <div className="sender-info">
              <div className="username">{message.username || '-'}</div>
              <code className="sender-id">{message.sender_id}</code>
            </div>
          </div>
          <div className="info-item">
            <label>Channel ID:</label>
            <code className="channel-id">{message.channel_id}</code>
          </div>
          <div className="info-item">
            <label>Created:</label>
            <span>{formatDate(message.create_time)}</span>
          </div>
          <div className="info-item">
            <label>Updated:</label>
            <span>{formatDate(message.update_time)}</span>
          </div>
        </div>
      </div>

      <div className="message-content-section">
        <div className="content-header">
          <h4>Message Content</h4>
          {editAllowed() && !editing && (
            <button
              type="button"
              className="btn btn-outline-primary edit-btn"
              onClick={handleEditMessage}
            >
              Edit Content
            </button>
          )}
        </div>

        {editing ? (
          <div className="edit-content-form">
            <textarea
              className="content-textarea"
              value={editContent}
              onChange={(e) => setEditContent(e.target.value)}
              rows={6}
              placeholder="Enter message content..."
            />
            <div className="edit-actions">
              <button
                type="button"
                className="btn btn-success"
                onClick={handleSaveEdit}
                disabled={saving || !editContent.trim()}
              >
                {saving ? <LoadingSpinner /> : 'Save Changes'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancelEdit}
                disabled={saving}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="message-content-display">
            <pre className="content-text">{message.content}</pre>
          </div>
        )}
      </div>

      {deleteAllowed() && (
        <div className="danger-zone">
          <h5>Danger Zone</h5>
          <p>Once you delete this message, there is no going back. Please be certain.</p>
          <button
            type="button"
            className="btn btn-danger"
            onClick={handleDeleteMessage}
          >
            Delete Message
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger">
          <RedTriangleIcon />
          <strong>Error:</strong> {error}
        </div>
      )}

      <ConfirmDialog
        isOpen={dialogState.isOpen}
        title={dialogState.title}
        message={dialogState.message}
        onConfirm={handleConfirm}
        onCancel={closeConfirmDialog}
      />
    </div>
  );
};

export default ChatMessageDetail;
